import { contextStore } from "../auth/context-store.js";

export interface RouteDefinition {
  path: string;
  component?: string;
  redirect?: string;
  requiredPermission?: string;
}

interface MatchResult {
  route: RouteDefinition;
  params: Record<string, string>;
}

class Router {
  private routes: RouteDefinition[] = [];
  private notFoundComponent: string = "app-error-state";
  private forbiddenComponent: string = "app-error-state";
  private rootElement: HTMLElement | null = null;
  private permissionChecker: (perm: string) => boolean = () => true;

  public init(
    routes: RouteDefinition[], 
    rootSelector: string,
    permissionChecker: (perm: string) => boolean
  ) {
    this.routes = routes;
    this.permissionChecker = permissionChecker;

    // Try to find the root element immediately
    this.rootElement = document.querySelector(rootSelector);
    
    // If not found, try again after a microtask (custom element upgrade may be pending)
    if (!this.rootElement) {
      console.warn(`[Router] Root element "${rootSelector}" not found immediately, scheduling retry...`);
      queueMicrotask(() => {
        this.rootElement = document.querySelector(rootSelector);
        if (!this.rootElement) {
          console.error(`[Router] Root element "${rootSelector}" still not found after microtask!`);
          // Last resort: wait for next frame
          requestAnimationFrame(() => {
            this.rootElement = document.querySelector(rootSelector);
            if (this.rootElement) {
              console.log(`[Router] Root element found after requestAnimationFrame`);
              this.handleRoute();
            } else {
              console.error(`[Router] FATAL: Root element "${rootSelector}" never found.`);
            }
          });
        } else {
          console.log(`[Router] Root element found after microtask`);
          this.handleRoute();
        }
      });
    }

    window.addEventListener("popstate", () => { this.handleRoute(); });
    window.addEventListener("hashchange", () => { this.handleRoute(); });
    // Reavaliação de rota quando o acesso efetivo muda dentro da MESMA
    // sessão (permissões alteradas no cadastro). Trocar de usuário não passa
    // mais por aqui: é logout + nova autenticação, com recarga da página.
    window.addEventListener("rbac-role-changed", () => { this.handleRoute(); });
    
    // Intercept clicks on links
    document.body.addEventListener("click", (e) => {
      const target = (e.target as HTMLElement).closest("a");
      if (target && target.matches("[data-link]")) {
        e.preventDefault();
        this.navigate(target.getAttribute("href") || "/");
      }
    });

    // Only call handleRoute if rootElement was found immediately
    if (this.rootElement) {
      this.handleRoute();
    }
  }

  public navigate(path: string) {
    window.history.pushState(null, "", path);
    this.handleRoute();
  }

  public revalidate() {
    this.handleRoute();
  }

  private matchRoute(pathname: string): MatchResult | null {
    // Clean path
    const cleanPath = pathname.split('?')[0] ?? '/';
    const normalized = cleanPath.replace(/\/+$/, '') || '/';

    for (const route of this.routes) {
      const routePath = route.path.replace(/\/+$/, '') || '/';
      if (routePath === normalized) {
        return { route, params: {} };
      }

      const rParts = routePath.split('/').filter(Boolean);
      const aParts = normalized.split('/').filter(Boolean);

      if (rParts.length !== aParts.length) {
        continue;
      }

      let matches = true;
      const params: Record<string, string> = {};

      for (let i = 0; i < rParts.length; i++) {
        const rp = rParts[i];
        const ap = aParts[i];
        if (!rp || !ap) {
          matches = false;
          break;
        }
        if (rp.startsWith(':')) {
          const paramName = rp.slice(1);
          params[paramName] = decodeURIComponent(ap);
        } else if (rp !== ap) {
          matches = false;
          break;
        }
      }

      if (matches) {
        return { route, params };
      }
    }

    return null;
  }

  private handleRoute() {
    console.log("[Router] handleRoute called");
    console.log("[Router] rootElement:", this.rootElement);
    if (!this.rootElement) {
      console.error("[Router] rootElement is NULL — #page-root not found!");
      return;
    }

    let path = window.location.pathname;
    if (window.location.hash && window.location.hash.startsWith('#/')) {
      path = window.location.hash.slice(1);
    }
    
    // Auth check
    const ctx = contextStore.get();
    if (!ctx.isAuthenticated && path !== "/login") {
      this.navigate("/login");
      return;
    }

    // Bypass login page if already authenticated (auto-login)
    if (ctx.isAuthenticated && path === "/login") {
      this.navigate("/");
      return;
    }

    console.log("[Router] current path:", path);
    const match = this.matchRoute(path);
    console.log("[Router] matched match:", match);

    this.rootElement.innerHTML = "";

    if (!match) {
      console.warn("[Router] No route matched, rendering 404");
      const el = document.createElement(this.notFoundComponent);
      el.setAttribute("code", "404");
      el.setAttribute("message", "Página não encontrada.");
      this.rootElement.appendChild(el);
      return;
    }

    const { route, params } = match;

    if (route.redirect) {
      console.log(`[Router] Redirecting from ${route.path} to ${route.redirect}`);
      window.history.replaceState(null, "", route.redirect);
      this.handleRoute();
      return;
    }

    if (route.requiredPermission && !this.permissionChecker(route.requiredPermission)) {
      console.warn("[Router] Permission denied for route:", route.path);
      const el = document.createElement(this.forbiddenComponent);
      el.setAttribute("code", "403");
      el.setAttribute("message", "Acesso negado. Você não tem permissão para visualizar este módulo.");
      this.rootElement.appendChild(el);
      return;
    }

    if (!route.component) {
      console.warn("[Router] Route matched but no component or redirect defined");
      return;
    }

    console.log("[Router] Creating component:", route.component);
    const component = document.createElement(route.component);
    // Passing path as an attribute in case the generic placeholder wants it
    component.setAttribute("path", path);

    // Pass all route params as attributes (e.g. :id -> id, report-id, etc.)
    for (const [key, val] of Object.entries(params)) {
      component.setAttribute(key, val);
      // Map kebab-case attribute if key is camelCase
      const kebab = key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
      component.setAttribute(kebab, val);
      if (key === 'id') {
        if (route.path.includes('/reports/')) {
          component.setAttribute('report-id', val);
        } else if (route.path.includes('/inspections/')) {
          component.setAttribute('inspection-id', val);
        } else if (route.path.includes('/equipment/')) {
          component.setAttribute('equipment-id', val);
        }
      }
    }

    this.rootElement.appendChild(component);
    console.log("[Router] Component appended. rootElement children:", this.rootElement.childElementCount);
    console.log("[Router] Component innerHTML length:", component.innerHTML.length);
  }
}

export const router = new Router();
